import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cross2Icon } from '@radix-ui/react-icons';
import { useMsal } from '@azure/msal-react';
import { useDispatch } from 'react-redux';
import { useTheme } from '../../components/Theme/ThemeProvider';
import { PromptLibraryCard } from './PromptLibraryCard';
import { PromptLibraryFilters } from './PromptLibraryFilters';
import { useUserCreatedPrompts } from './useSavedPrompts';
import { getAllPrompts } from './promptLibraryData';
import { PromptAgentType, PromptLibraryItem, UserCreatedPrompt } from './types';
import { BookmarkPromptModal } from './BookmarkPromptModal';
import { setChatMode, setRagChatType, setPromptFromLibrary, type RagChatType } from '../../store/chatModeSlice';
import FeedbackToast from '../../components/Chat/FeedbackToast';

interface PromptLibraryProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (prompt: string, tool?: string, agentType?: PromptAgentType) => void;
}

type Tab = 'suggested' | 'saved';

export const PromptLibrary: React.FC<PromptLibraryProps> = ({
  isOpen,
  onClose,
  onSelect,
}) => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const { instance: msalInstance } = useMsal();
  const dispatch = useDispatch();

  const [selectedAgentTypes, setSelectedAgentTypes] = useState<PromptAgentType[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<Tab>('suggested');
  const [toastState, setToastState] = useState<{ show: boolean; message: string; variant: 'success' | 'error' }>({ show: false, message: '', variant: 'success' });
  const { userPrompts, fetchUserPrompts, saveUserPrompt, updateUserPrompt, deleteUserPrompt } = useUserCreatedPrompts();
  const isSaved = useCallback((prompt: string) => userPrompts.some((p) => p.prompt === prompt), [userPrompts]);
  const [editingPrompt, setEditingPrompt] = useState<{ id: string; title: string; prompt: string; agentType: PromptAgentType } | null>(null);

  const showToast = (message: string, variant: 'success' | 'error') =>
    setToastState({ show: true, message, variant });

  const handleToggleSaved = async (item: PromptLibraryItem) => {
    const wasSaved = isSaved(item.prompt);
    if (!wasSaved) {
      if (msalInstance) {
        try {
          await saveUserPrompt(msalInstance, {
            title: item.title,
            prompt: item.prompt,
            agentType: item.agentType,
            category: item.category,
          });
          showToast('Prompt bookmarked! Find it in "My saved prompts".', 'success');
        } catch {
          showToast('Failed to bookmark prompt. Please try again.', 'error');
        }
      }
    } else {
      if (msalInstance) {
        const matching = userPrompts.find((p) => p.prompt === item.prompt);
        if (matching?.id) {
          try {
            await deleteUserPrompt(msalInstance, matching.id);
            showToast('Prompt removed from saved.', 'success');
          } catch {
            showToast('Failed to remove prompt. Please try again.', 'error');
          }
        }
      }
    }
  };

  // Fetch user-created prompts when the library opens
  useEffect(() => {
    if (isOpen && msalInstance) {
      fetchUserPrompts(msalInstance);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const allPrompts = useMemo(() => getAllPrompts(), []);

  const filteredPrompts = useMemo(() => {
    let items: PromptLibraryItem[] = allPrompts;

    // Filter by tab
    if (activeTab === 'saved') {
      items = userPrompts.map((up) => ({
        title: up.title,
        prompt: up.prompt,
        agentType: up.agentType,
        _userCreatedId: up.id,
      } as PromptLibraryItem & { _userCreatedId?: string }));
    }

    // Filter by agent types (empty array means all)
    if (selectedAgentTypes.length > 0) {
      items = items.filter((p) => selectedAgentTypes.includes(p.agentType));
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter((p) =>  p.title.toLowerCase().includes(q));
    }

    return items;
  }, [allPrompts, activeTab, selectedAgentTypes, searchQuery, userPrompts]);

  const handleSelect = (prompt: string, tool?: string, agentType?: PromptAgentType) => {
    // If parent provided an onSelect callback, delegate to it (e.g. for confirmation flow)
    if (onSelect) {
      onSelect(prompt, tool, agentType);
      return;
    }
    // Dispatch prompt to Redux so ChatInput can consume it
    dispatch(setPromptFromLibrary({ text: prompt, tool }));
    // Switch RAG mode if selecting a non-Ask-Chat agent
    if (agentType && agentType !== 'Ask Chat') {
      dispatch(setRagChatType(agentType as RagChatType));
    } else {
      dispatch(setChatMode('Ask Chat'));
    }
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="prompt-library-overlay"
          initial={{ opacity: 0, scale: 0.93 }}
          animate={{ opacity: 1, scale: 1, transition: { duration: 0.25, ease: [0.0, 0.0, 0.2, 1] } }}
          exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.25, ease: [0.4, 0.0, 0.2, 1] } }}
          className="absolute inset-0 flex flex-col overflow-hidden"
          style={{
            zIndex: 100,
            backgroundColor: 'var(--main-background)',
            transformOrigin: 'bottom left',
            willChange: 'transform, opacity',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-6 py-4 flex-shrink-0"
            style={{
              borderBottom: `1px solid var(--color-border)`,
            }}
          >
            <h1
              className="text-xl font-bold"
              style={{
                color: 'var(--color-text)',
                fontFamily: '3MCircularTT, sans-serif',
              }}
            >
              Prompt Library
            </h1>


            <div className="flex items-center gap-3">
              {/* Tab buttons */}
              <button
                className="px-4 py-1.5 rounded-md text-xs font-semibold transition-colors"
                style={{
                  backgroundColor:
                    activeTab === 'suggested'
                      ? '#ef4444'
                      : isDark
                        ? '#2a2a2e'
                        : '#e5e7eb',
                  color:
                    activeTab === 'suggested'
                      ? '#ffffff'
                      : isDark
                        ? '#a1a1aa'
                        : '#6b7280',
                  border:
                    activeTab === 'suggested'
                      ? 'none'
                      : `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)'}`,
                  fontFamily: '3MCircularTT, sans-serif',
                }}
                onClick={() => setActiveTab('suggested')}
              >
                Suggested prompts
              </button>
              <button
                className="px-4 py-1.5 rounded-md text-xs font-semibold transition-colors"
                style={{
                  backgroundColor:
                    activeTab === 'saved'
                      ? '#ef4444'
                      : isDark
                        ? '#2a2a2e'
                        : '#e5e7eb',
                  color:
                    activeTab === 'saved'
                      ? '#ffffff'
                      : isDark
                        ? '#a1a1aa'
                        : '#6b7280',
                  border:
                    activeTab === 'saved'
                      ? 'none'
                      : `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)'}`,
                  fontFamily: '3MCircularTT, sans-serif',
                }}
                onClick={() => setActiveTab('saved')}
              >
                My saved prompts
              </button>

              {/* Close button */}
              <button
                className="p-1.5 rounded-md transition-colors hover:bg-black/10 dark:hover:bg-white/10"
                onClick={onClose}
                title="Close"
              >
                <Cross2Icon
                  width={18}
                  height={18}
                  style={{ color: isDark ? '#a1a1aa' : '#6b7280' }}
                />
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="px-6 py-4 flex-shrink-0">
            <PromptLibraryFilters
              selectedAgentTypes={selectedAgentTypes}
              onSelectedAgentTypesChange={setSelectedAgentTypes}
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
            />
          </div>

          {/* Card grid */}
          <div
            className="flex-1 overflow-y-auto px-6 pb-6 chat-page-scroll"
            style={{ scrollBehavior: 'smooth', paddingTop: '8px', marginTop: '-8px' }}
          >
            {filteredPrompts.length === 0 ? (
              <div
                className="flex items-center justify-center h-48"
                style={{
                  color: isDark ? '#71717a' : '#9ca3af',
                  fontFamily: '3MCircularTT, sans-serif',
                }}
              >
                <p className="text-sm">
                  {activeTab === 'saved'
                    ? 'No saved prompts yet. Bookmark prompts to see them here.'
                    : 'No prompts match your filters.'}
                </p>
              </div>
            ) : (
              (() => {
                // Group prompts by category (items without category go into 'uncategorized')
                const categorized: { category: string | null; items: typeof filteredPrompts }[] = [];
                const uncategorized: typeof filteredPrompts = [];
                const categoryMap = new Map<string, typeof filteredPrompts>();

                for (const item of filteredPrompts) {
                  if (item.category) {
                    const existing = categoryMap.get(item.category);
                    if (existing) {
                      existing.push(item);
                    } else {
                      categoryMap.set(item.category, [item]);
                    }
                  } else {
                    uncategorized.push(item);
                  }
                }

                // Preserve insertion order for categories
                categoryMap.forEach((items, category) => {
                  categorized.push({ category, items });
                });

                return (
                  <div>
                    {/* Uncategorized items first */}
                    {uncategorized.length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
                        {uncategorized.map((item, idx) => {
                          const userCreatedId = (item as any)._userCreatedId as string | undefined;
                          return (
                            <PromptLibraryCard
                              key={userCreatedId || `${item.agentType}-uncategorized-${idx}`}
                              item={item}
                              isSaved={isSaved(item.prompt)}
                              onToggleSaved={() => handleToggleSaved(item)}
                              onClick={(prompt, tool) => handleSelect(prompt, tool, item.agentType)}
                              isUserCreated={!!userCreatedId}
                              onDelete={userCreatedId ? async () => {
                                if (msalInstance) {
                                  try {
                                    await deleteUserPrompt(msalInstance, userCreatedId);
                                    showToast('Prompt deleted.', 'success');
                                  } catch {
                                    showToast('Failed to delete prompt. Please try again.', 'error');
                                  }
                                }
                              } : undefined}
                              onEdit={userCreatedId ? () => {
                                setEditingPrompt({ id: userCreatedId, title: item.title, prompt: item.prompt, agentType: item.agentType });
                              } : undefined}
                            />
                          );
                        })}
                      </div>
                    )}

                    {/* Categorized items with section headers */}
                    {categorized.map(({ category, items }) => (
                      <div key={category!} className="mb-6">
                        <h2
                          className="text-sm font-semibold uppercase tracking-wider mb-3 px-1"
                          style={{
                            color: isDark ? '#a1a1aa' : '#6b7280',
                            fontFamily: '3MCircularTT, sans-serif',
                            borderBottom: `1px solid ${'var(--color-border)'}`,
                            paddingBottom: '8px',
                          }}
                        >
                          {category}
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                          {items.map((item, idx) => {
                            const userCreatedId = (item as any)._userCreatedId as string | undefined;
                            return (
                              <PromptLibraryCard
                                key={userCreatedId || `${item.agentType}-${category}-${idx}`}
                                item={item}
                                isSaved={isSaved(item.prompt)}
                                onToggleSaved={() => handleToggleSaved(item)}
                                onClick={(prompt, tool) => handleSelect(prompt, tool, item.agentType)}
                                isUserCreated={!!userCreatedId}
                                onDelete={userCreatedId ? async () => {
                                  if (msalInstance) {
                                    try {
                                      await deleteUserPrompt(msalInstance, userCreatedId);
                                      showToast('Prompt deleted.', 'success');
                                    } catch {
                                      showToast('Failed to delete prompt. Please try again.', 'error');
                                    }
                                  }
                                } : undefined}
                              onEdit={userCreatedId ? () => {
                                setEditingPrompt({ id: userCreatedId, title: item.title, prompt: item.prompt, agentType: item.agentType });
                              } : undefined}
                              />
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()
            )}
          </div>

          {/* Toast notifications for CRUD operations */}
          <div className="absolute bottom-6 inset-x-6" style={{ zIndex: 200, pointerEvents: 'none' }}>
            <div style={{ pointerEvents: 'auto' }}>
              <FeedbackToast
                show={toastState.show}
                onDismiss={() => setToastState((s) => ({ ...s, show: false }))}
                message={toastState.message}
                variant={toastState.variant}
              />
            </div>
          </div>

          {/* Edit Prompt Modal */}
          <BookmarkPromptModal
            isOpen={!!editingPrompt}
            onClose={() => setEditingPrompt(null)}
            onSave={async (updated: Omit<UserCreatedPrompt, 'id' | 'createdAt'>) => {
              if (editingPrompt && msalInstance) {
                try {
                  await updateUserPrompt(msalInstance, editingPrompt.id, updated);
                  showToast('Prompt updated successfully.', 'success');
                } catch {
                  showToast('Failed to update prompt. Please try again.', 'error');
                }
              }
              setEditingPrompt(null);
            }}
            initialTitle={editingPrompt?.title}
            initialDescription={editingPrompt?.prompt}
            initialAgentType={editingPrompt?.agentType}
            isEditMode
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};
